
import { Link } from "react-router-dom";
import { SkillBadge } from "./SkillBadge";
import { FileText, Circle, AlertTriangle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useRef } from "react";
import { CVFullScreenModal } from "@/components/candidates/CVFullScreenModal";
import { supabase } from "@/integrations/supabase/client";
import { Candidate } from "@/types/candidate";
import { EntityCard } from "./common/EntityCard";
import { checkCVExists } from "@/utils/candidate/cv-storage";
import { cn } from "@/lib/utils";

interface CandidateCardProps {
  candidate: Candidate;
  className?: string;
}

export const CandidateCard = ({ candidate, className }: CandidateCardProps) => {
  const [cvUrl, setCvUrl] = useState<string | null>(null);
  const [showCVModal, setShowCVModal] = useState(false);
  const [cvExists, setCvExists] = useState(false);
  const skillsContainerRef = useRef<HTMLDivElement>(null);
  const [maxVisibleSkills, setMaxVisibleSkills] = useState<number>(4);
  const [matchCount, setMatchCount] = useState<number>(0);

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "looking":
        return "text-green-500";
      case "open":
        return "text-orange-400";
      case "unavailable":
        return "text-red-500";
      default:
        return "text-gray-400";
    }
  };

  // Fetch the actual match count for this candidate, excluding "Refused" matches
  useEffect(() => {
    const fetchMatchCount = async () => {
      try {
        const { count, error } = await supabase
          .from('matches')
          .select('id', { count: 'exact', head: false })
          .eq('candidate_id', candidate.id)
          .neq('status', 'refused');
          
        if (error) {
          console.error("Error fetching match count:", error);
          return;
        }
        
        setMatchCount(count || 0);
      } catch (err) {
        console.error("Failed to fetch match count:", err);
      }
    };
    
    fetchMatchCount();
  }, [candidate.id]);

  useEffect(() => {
    const checkForCV = async () => {
      const exists = await checkCVExists(candidate.id);
      setCvExists(exists);
    };
    
    checkForCV();
  }, [candidate.id]);

  useEffect(() => {
    if (!skillsContainerRef.current) return;
    const container = skillsContainerRef.current;
    const badges = Array.from(container.children) as HTMLElement[];
    if (badges.length === 0) return;

    // Reset all badges to display so we can measure
    badges.forEach(badge => badge.style.display = "");

    let totalHeight = 0;
    let lastBottom = 0;
    let visibleCount = 0;
    const maxHeight = 64; // px, adjust as needed (e.g. 2 lines)

    for (let i = 0; i < badges.length; i++) {
      const badge = badges[i];
      const rect = badge.getBoundingClientRect();
      if (i === 0) lastBottom = rect.bottom;
      // If badge is on a new line, increase totalHeight
      if (rect.top > lastBottom) {
        totalHeight += rect.height;
        lastBottom = rect.bottom;
      }
      if (totalHeight + rect.height > maxHeight) {
        break;
      }
      visibleCount++;
    }
    setMaxVisibleSkills(visibleCount);
  }, [candidate.skills]);

  const handleViewCV = async () => {
    if (cvExists) {
      try {
        const { data: resumeList, error: errorFetchResume } = await supabase.functions.invoke("get-candidate-resume", {
          body: {
            candidateId: candidate.id
          }
        });
    
        if (resumeList === null) {
          return;
        }

        setCvUrl(resumeList.resume.currentResume.publicUrl);
        setShowCVModal(true);

      } catch (error) {
        console.error("Error fetching CV:", error);
      }
    }
  };

  const lowercaseStatus = candidate.status?.toLowerCase();

  return (
    <div className={cn(
      "overflow-hidden transition-all duration-300 hover:shadow-lg backdrop-blur-sm bg-white/80 border border-white/20 rounded-xl",
      "hover:bg-white/90 hover:scale-[1.02] hover:border-white/30",
      "dark:bg-gray-800/80 dark:border-gray-700/20 dark:hover:bg-gray-800/90 dark:hover:border-gray-700/30",
      className
    )}>
      <div className="p-4 border-b border-border">
        <Link to={`/candidates/${candidate.id}`} className="hover:text-primary transition-colors">
          <h3 className="text-lg font-semibold truncate mb-1 text-gray-900 dark:text-gray-100">{candidate.name}</h3>
        </Link>
      </div>
      
      <div className="flex-1 p-4">
        <div className="flex flex-col h-full">
          <div className="h-8">
            {candidate.function && (
              <span className="text-sm text-gray-500 dark:text-gray-400">{candidate.function}</span>
            )}
          </div>
          
          <div className="my-2">
            <div className="space-y-2">
              <div className="flex items-center space-x-1">
                <span className="text-sm text-gray-500 dark:text-gray-400">Email:</span>
                <a href={`mailto:${candidate.email}`} className="text-sm text-blue-600 hover:underline truncate dark:text-blue-400">
                  {candidate.email}
                </a>
              </div>
              
              <div className="flex items-center space-x-1">
                <span className="text-sm text-gray-500 dark:text-gray-400">Phone:</span>
                {candidate.phone ? (
                  <a href={`tel:${candidate.phone}`} className="text-sm text-blue-600 hover:underline truncate dark:text-blue-400 flex items-center">
                    <Phone className="h-3 w-3 mr-1" />
                    {candidate.phone}
                  </a>
                ) : (
                  <span className="text-sm text-gray-400 dark:text-gray-500">Not provided</span>
                )}
              </div>
            </div>
            
            {candidate.skills && candidate.skills.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3" ref={skillsContainerRef} style={{maxHeight: 64, overflow: 'hidden'}}>
                {(() => {
                  const skillsToShow = candidate.skills.length > maxVisibleSkills
                    ? candidate.skills.slice(0, maxVisibleSkills - 1)
                    : candidate.skills.slice(0, maxVisibleSkills);
                  return (
                    <>
                      {skillsToShow.map((skill) => (
                        <SkillBadge key={skill} label={skill} />
                      ))}
                      {candidate.skills.length > maxVisibleSkills && (
                        <SkillBadge
                          key="more-badge"
                          label={`+${candidate.skills.length - (maxVisibleSkills - 1)} more`}
                          className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                        />
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          
          <div className="mt-auto h-20">
            <div className="h-6">
              {candidate.status && (
                <div className="flex items-center">
                  <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">Status:</span>
                  <div className="flex items-center gap-1.5">
                    <Circle className={`h-2.5 w-2.5 fill-current ${getStatusColor(candidate.status)}`} />
                    <span className="text-xs dark:text-gray-300">{lowercaseStatus}</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="h-12 flex items-center">
              <div className="flex justify-between items-center w-full">
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">Matches:</span>
                  <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 h-8 w-8 rounded-md flex items-center justify-center text-sm font-medium">
                    {matchCount}
                  </div>
                </div>
                
                {cvExists ? (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleViewCV}
                    className="flex items-center gap-1 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm border-gray-200/50 dark:border-gray-600/50 hover:bg-white/70 dark:hover:bg-gray-700/70"
                  >
                    <FileText className="h-4 w-4" />
                    CV
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex items-center gap-1 border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-500 dark:text-orange-400 dark:hover:bg-orange-900/30"
                  >
                    <AlertTriangle className="h-4 w-4" />
                    No CV
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCVModal && cvUrl && (
        <CVFullScreenModal
          cvUrl={cvUrl}
          onClose={() => setShowCVModal(false)}
        />
      )}
    </div>
  );
};
